import { Box, Button, Image, Link, Text, useToast } from '@chakra-ui/react';
import { BrokerType, brokerTypesData } from 'enums/shared/brokerType';
import { Form, Formik } from 'formik';
import useBrokerLogo from 'hooks/modules/brokerAccount/useBrokerLogo';
import useFormError from 'hooks/shared/useFormError';
import ConnectBrokerInstructionItem from 'presentation/components/modules/brokerAccount/create/ConnectBrokerInstructionItem';
import AppCard from 'presentation/components/shared/card/AppCard';
import FormTextInput from 'presentation/components/shared/form/FormTextInput';
import GridHeading from 'presentation/components/shared/grid/GridHeading';
import SettingsLayout from 'presentation/layout/modules/settings/SettingsLayout';
import { Color } from 'presentation/theme/colorScheme';
import { FC } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AppRoute from 'router/AppRoute';
import { useCreateBrokerAccountMutation } from 'rtkQuery/sharespro/queries/brokerAccounts';
import * as Yup from 'yup';

interface FormValues {
  public_key: string;
  secret_key: string;
}

const ConnectFinamBrokerPage: FC = () => {
  const { t } = useTranslation();

  const brokerLogo = useBrokerLogo({ isFullLogo: true });

  const [createBroker, { isLoading }] = useCreateBrokerAccountMutation();

  const navigate = useNavigate();

  const formErrorHandler = useFormError<FormValues>();

  const toast = useToast();

  const validationSchema = Yup.object().shape({
    public_key: Yup.string().required(t('fieldIsRequired') ?? ''),
    secret_key: Yup.string().required(t('fieldIsRequired') ?? ''),
  });

  const title = t('connectFinam');

  return (
    <SettingsLayout>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <GridHeading>{title}</GridHeading>
      <AppCard p="40px" mb="20px" w="100%">
        <Image src={brokerLogo({ brokerType: BrokerType.Finam })} w="200px" mb="20px" ml="120px" />
        <Formik<FormValues>
          initialValues={{
            public_key: '',
            secret_key: '',
          }}
          onSubmit={(values, formik) => {
            createBroker({
              broker_id: brokerTypesData[BrokerType.Finam].id,
              public_key: values.public_key,
              secret_key: values.secret_key,
            })
              .unwrap()
              .then(() => {
                toast({
                  description: t('brokerAccountWasCreated'),
                  status: 'success',
                  position: 'top-right',
                  isClosable: true,
                });
                navigate(AppRoute.portfolio);
              })
              .catch((err) => formErrorHandler(err, formik));
          }}
          validationSchema={validationSchema}
        >
          {({ handleChange, values }) => {
            const steps = [
              <Text key={0}>
                {t('finamBrokerConnectStep1_1')}
                <Link
                  href="https://www.comon.ru/"
                  textDecoration="underline"
                  isExternal
                >
                  {t('finamBrokerConnectStep1_2')}
                </Link>
                {t('finamBrokerConnectStep1_3')}
              </Text>,
              <Text key={1}>
                {t('finamBrokerConnectStep2_1')}
                <Link
                  href="https://www.comon.ru/my/trade-api/tokens/"
                  textDecoration="underline"
                  isExternal
                >
                  {t('finamBrokerConnectStep2_2')}
                </Link>
                {t('finamBrokerConnectStep2_3')}
              </Text>,
              <Text key={2}>{t('finamBrokerConnectStep3')}</Text>,
              <Text key={3}>{t('finamBrokerConnectStep4')}</Text>,
              <Box key={4} pt="10px">
                <Text mb="35px">{t('finamBrokerConnectStep5')}</Text>
                <FormTextInput
                  name="public_key"
                  onChange={handleChange}
                  value={values.public_key}
                  w="364px"
                  placeholder={t('finamAccountField') ?? ''}
                  autoComplete="off"
                />
              </Box>,
              <Box key={5} pt="10px">
                <Text mb="35px">{t('finamBrokerConnectStep6')}</Text>
                <FormTextInput
                  name="secret_key"
                  w="364px"
                  onChange={handleChange}
                  value={values.secret_key}
                  type="password"
                  placeholder={t('finamTokenField') ?? ''}
                  autoComplete="off"
                />
              </Box>,
            ];

            return (
              <Form>
                <Box position="relative" w="100%" mb="35px">
                  <Box w="100%">
                    {steps.map((stepText, index) => (
                      <Box key={index} mb="25px">
                        <ConnectBrokerInstructionItem
                          hasFormFields={index > 3}
                          step={index + 1}
                        >
                          {stepText}
                        </ConnectBrokerInstructionItem>
                      </Box>
                    ))}
                  </Box>
                  <Box
                    position="absolute"
                    left="78px"
                    top="0"
                    bgColor={Color.Shade100}
                    w="2px"
                    h="100%"
                  />
                </Box>
                <Button ml="120px" type="submit" isLoading={isLoading}>
                  {t('continuePhrase')}
                </Button>
              </Form>
            );
          }}
        </Formik>
      </AppCard>
    </SettingsLayout>
  );
};

export default ConnectFinamBrokerPage;
